// Pure FilterState logic: scoping a filter to the charts it applies to
// (design doc §4), converting a control's selection into the wire shape, and
// round-tripping the whole state through the `?filters=` URL param. No
// React/store wiring here: the live store is `lib/filter-store.ts`.
import type {
  ChartSpec,
  DatasetSpec,
  FilterOperator,
  FilterState,
  NativeFilterSpec,
  RuntimeFilterOp,
  RuntimeFilterValue,
} from "@bi/shared/types";

import { DEFAULT_PRESET_DAYS, boundPrecision, isValidCustomRange, presetRange } from "./time-range";

/** An empty `FilterState` — no runtime overrides, no time-range override.
 *  The seed for a fresh load with no `?filters=` param and the identity
 *  value `setFilterValue`/`clearFilterValue` fold against. */
export function emptyFilterState(): FilterState {
  return { values: [], time_range_start: null, time_range_end: null, parameters: {} };
}

/** Old lifecycle links selected a precomputed partition while ignoring dates.
 * Preserve that window when opening them against the new input-cohort query.
 */
export function migrateLegacyCohortWindow(
  state: FilterState,
  datasets: Record<string, DatasetSpec>,
): FilterState {
  if (
    !Object.values(datasets).some(
      (d) => d.table_name === "admin_lifecycle_funnel" && d.cohort_filters,
    )
  )
    return state;
  const legacy = state.values?.find((v) => v.column === "cohort_window");
  if (!legacy) return state;
  const value = Array.isArray(legacy.value) ? legacy.value[0] : legacy.value;
  const days = value === "7d" ? 7 : value === "30d" ? 30 : value === "90d" ? 90 : null;
  const range = presetRange(days);
  return {
    ...state,
    values: state.values?.filter((v) => v.column !== "cohort_window"),
    time_range_start: range.start,
    time_range_end: range.end,
  };
}

/** The state a dashboard opens on when the URL carries no `?filters=` — the
 *  last 7 days, not all time.
 *
 *  Distinct from `emptyFilterState` on purpose, and only one of the two is a
 *  default: this is the SEED for a fresh load, while `emptyFilterState` stays
 *  the identity value that folds and parse failures need (a state carrying a
 *  real 7-day window is not an identity, and using it as one would make
 *  "clear every filter" quietly re-apply a window).
 *
 *  All time is the wrong thing to open on. It is the widest, slowest, and
 *  least current answer a dashboard can give, it makes every per-day chart
 *  render hundreds of buckets wide enough to be unreadable, and a reader who
 *  wants the whole history can still ask for it in one click. Seven days is
 *  the window someone opening a dashboard almost always means. */
export function defaultFilterState(): FilterState {
  const range = presetRange(DEFAULT_PRESET_DAYS);
  return { values: [], time_range_start: range.start, time_range_end: range.end, parameters: {} };
}

/** The time-range control every dashboard gets, whether or not its author
 *  declared one (e.g. "LLM Usage" declares none). The server applies
 *  `time_range_start`/`_end` to any chart whose dataset resolves a temporal
 *  column, whatever the dashboard declares (`orchestration.py`'s
 *  `scope_filters_for_chart`), so only the control was missing.
 *
 *  Synthesized rather than special-cased in the render, so it takes the same
 *  scope/inactive path as an authored filter: on a dashboard with no date
 *  column it renders MUTED instead of vanishing. */
const IMPLICIT_TIME_FILTER: NativeFilterSpec = {
  id: "__implicit_time_range__",
  name: "Time range",
  kind: "time_range",
  enable_empty_filter: false,
  multi_select: true,
  options: [],
  scope_chart_uuids: [],
  target_column: null,
  target_dataset_uuid: null,
  target_parameter_low: null,
  target_parameter_high: null,
};

/** The authored filters, with a time-range control guaranteed to be among
 *  them. An authored `time_range` filter always wins (it carries the author's
 *  own name for the control, and its scope overrides). */
export function withImplicitTimeFilter(
  filters: readonly NativeFilterSpec[],
): readonly NativeFilterSpec[] {
  return filters.some((f) => f.kind === "time_range")
    ? filters
    : [IMPLICIT_TIME_FILTER, ...filters];
}

/**
 * The authored opening selection for every `select` filter that declares one,
 * folded into `state`. An existing value for a column always wins, so this
 * only ever FILLS gaps.
 *
 * Needed for a filter whose column PARTITIONS its dataset (disjoint
 * pre-computed windows, say): with no selection, the first paint unions every
 * partition into one chart, and there is no honest rendering of that.
 *
 * Superset stores the value as `defaultDataMask.filterState.value`, a LIST for
 * a select filter. A bare scalar is read as a one-element list (hand-authored
 * specs use it). Anything else (an object, a null, an empty list) is skipped.
 *
 * Applied only on a load with no `?filters=` param: a shared link is a snapshot
 * of what its sender saw, and adding a selection they had removed would change it.
 */
export function withDefaultSelections(
  state: FilterState,
  filters: readonly NativeFilterSpec[],
): FilterState {
  let next = state;
  for (const filter of filters) {
    if (filter.kind !== "select" || !filter.target_column) continue;
    if (getFilterValue(next, filter.target_column)) continue;
    const raw = filter.default_value;
    const values = (Array.isArray(raw) ? raw : raw === null || raw === undefined ? [] : [raw])
      .filter((v): v is string | number | boolean =>
        ["string", "number", "boolean"].includes(typeof v),
      )
      .map(String);
    if (values.length === 0) continue;
    const value = filterValueForSelection(filter, values);
    if (value) next = setFilterValue(next, value.column, value.op, value.value);
  }
  return next;
}

/** True iff the picked window IS the default one a fresh load opens on.
 *
 *  This is what "no zoom applied" means now that the default is a real 7-day
 *  window. Do not test "are any bounds set?": that is true on every load.
 *
 *  A shared link carrying YESTERDAY's 7-day window reads as non-default: it is
 *  a different window now, so the reader is offered the way back. */
export function isDefaultTimeRange(
  state: Pick<FilterState, "time_range_start" | "time_range_end">,
): boolean {
  const range = presetRange(DEFAULT_PRESET_DAYS);
  return state.time_range_start === range.start && state.time_range_end === range.end;
}

/** Back to the window a fresh load opens on. NOT `clearTimeRange` — clearing
 *  means "All time", which is a deliberate destination a reader can pick from
 *  the filter bar, not the place an undo should land them. */
export function resetTimeRangeToDefault(state: FilterState): FilterState {
  const range = presetRange(DEFAULT_PRESET_DAYS);
  return setTimeRange(state, range.start, range.end);
}

/** True iff `state` carries no runtime override at all (no column values,
 *  no time-range override) — the dashboard reads exactly as authored. */
export function isFilterStateEmpty(state: FilterState): boolean {
  return (
    (state.values?.length ?? 0) === 0 &&
    !state.time_range_start &&
    !state.time_range_end &&
    Object.keys(state.parameters ?? {}).length === 0
  );
}

/** The live value for `column`, or `undefined` if nothing is currently
 *  applied to it. */
export function getFilterValue(state: FilterState, column: string): RuntimeFilterValue | undefined {
  return (state.values ?? []).find((v) => v.column === column);
}

/** Sets (replacing any existing) the runtime override for `column`. Only one
 *  `RuntimeFilterValue` per column ever exists in a `FilterState`: the compiler
 *  ANDs every entry it is given (backend `FilterState` docstring), so a stale
 *  second entry for the same column would be ANDed against the new one, and two
 *  different values for one column match nothing. */
export function setFilterValue(
  state: FilterState,
  column: string,
  op: RuntimeFilterOp,
  value: unknown,
): FilterState {
  const rest = (state.values ?? []).filter((v) => v.column !== column);
  return { ...state, values: [...rest, { column, op, value }] };
}

/** Removes any runtime override for `column`, leaving every other value and
 *  the time-range override untouched. */
export function clearFilterValue(state: FilterState, column: string): FilterState {
  return { ...state, values: (state.values ?? []).filter((v) => v.column !== column) };
}

/** Sets both time-range bounds verbatim. Each may be a bare day
 *  ("2026-08-01") or a real moment ("2026-08-01T14:30:00"), but the two must
 *  carry the SAME precision: the backend refuses a mixed pair
 *  (`MixedTimeRangePrecision`, `service/bi/core/compiler.py`). Not re-checked
 *  here: every in-app caller produces a matching pair, and `parseFilterState`
 *  re-validates a pair arriving from a `?filters=` link. */
export function setTimeRange(
  state: FilterState,
  start: string | null,
  end: string | null,
): FilterState {
  return { ...state, time_range_start: start, time_range_end: end };
}

export function clearTimeRange(state: FilterState): FilterState {
  return { ...state, time_range_start: null, time_range_end: null };
}

// ── dataset parameters (a `range` native filter's two bounds) ───────────────
//
// A `range` filter does not write a `values` entry: its target is a PAIR of the
// dataset's declared `parameters`, which the dataset's own SQL reads as `@name`
// INSIDE its aggregation (see the service's `DatasetParameter`). The reader's
// bounds travel in `FilterState.parameters`, keyed by parameter name, and an
// absent key means "the dataset's declared default" — so the default lives in
// exactly one place (the dataset) and this module never copies it into state.

export function getParameterOverride(state: FilterState, name: string): number | null | undefined {
  return state.parameters?.[name];
}

export function setParameterOverrides(
  state: FilterState,
  overrides: Readonly<Record<string, number | null>>,
): FilterState {
  return { ...state, parameters: { ...(state.parameters ?? {}), ...overrides } };
}

export function clearParameterOverrides(state: FilterState, names: readonly string[]): FilterState {
  const rest = { ...(state.parameters ?? {}) };
  for (const name of names) delete rest[name];
  return { ...state, parameters: rest };
}

export interface RangeBounds {
  /** The bound in force: the reader's override, else the dataset's default. */
  low: number | null;
  high: number | null;
  defaultLow: number | null;
  defaultHigh: number | null;
  /** True when either bound differs from the dataset's declared default. */
  overridden: boolean;
}

/** What a `range` filter's control shows and applies. `undefined` when the
 *  filter is not a range, names no dataset, or the dataset does not declare
 *  both parameters — every one of which is an authoring defect the service's
 *  validator refuses, so a live spec never lands here; the control renders
 *  nothing rather than a pair of boxes bound to nothing. */
export function rangeBounds(
  filter: NativeFilterSpec,
  datasets: Record<string, DatasetSpec>,
  state: FilterState,
): RangeBounds | undefined {
  if (filter.kind !== "range") return undefined;
  const lowName = filter.target_parameter_low;
  const highName = filter.target_parameter_high;
  if (!lowName || !highName || !filter.target_dataset_uuid) return undefined;
  const dataset = datasets[filter.target_dataset_uuid];
  const lowParam = dataset?.parameters.find((p) => p.name === lowName);
  const highParam = dataset?.parameters.find((p) => p.name === highName);
  if (!lowParam || !highParam) return undefined;
  const lowOverride = getParameterOverride(state, lowName);
  const highOverride = getParameterOverride(state, highName);
  const low = lowOverride === undefined ? lowParam.default : lowOverride;
  const high = highOverride === undefined ? highParam.default : highOverride;
  return {
    low,
    high,
    defaultLow: lowParam.default,
    defaultHigh: highParam.default,
    overridden: low !== lowParam.default || high !== highParam.default,
  };
}

/**
 * Converts a `select`-kind `NativeFilterSpec`'s current selection into the
 * `RuntimeFilterValue` it should apply — or `undefined` when the filter
 * should simply not apply at all. `NativeFilterSpec.enable_empty_filter`
 * decides what an empty selection MEANS, and the two readings are opposite:
 *
 * - `false` (the common case): nothing selected → no filter. The control is
 *   "off", every row passes, same as if the filter didn't exist.
 * - `true` (rare, but must be respected — never silently treated as false):
 *   nothing selected → match NOTHING. Encoded as an `in` filter over an
 *   empty value list, which the compiler binds as an always-false predicate.
 *
 * Returns `undefined` (never applies) for a filter with no `target_column`
 * — the type allows `null`, though every real `select` filter in the
 * corpus carries one; a filter that names no column has nothing to bind a
 * `RuntimeFilterValue` to.
 */
export function filterValueForSelection(
  filter: NativeFilterSpec,
  selected: readonly string[],
): RuntimeFilterValue | undefined {
  if (!filter.target_column) return undefined;
  if (selected.length === 0) {
    return filter.enable_empty_filter
      ? { column: filter.target_column, op: "in", value: [] }
      : undefined;
  }
  return { column: filter.target_column, op: "in", value: [...selected] };
}

/**
 * A filter's live scope: every chart on the CURRENT dashboard whose dataset
 * declares the filter's `target_column`, unless `scope_chart_uuids` is a
 * non-empty AUTHORED override, in which case it is exactly that list, always
 * active (design doc §4; `spec.py`'s docstring on `scope_chart_uuids`).
 *
 * A `time_range` filter has no `target_column`, so it is active on every chart
 * unless an authored override says otherwise: an inactive time-range control is
 * a worse UX bug than one that does not reach a tile or two. That is safe
 * because the SERVER scopes it: `orchestration.scope_filters_for_chart` drops
 * the range for any chart whose `resolve_temporal_column` is None, since
 * `compile_chart_query` refuses a real time range with nothing to bind it
 * against (`MissingTemporalColumn`). `chartIgnoresDateRange` below tells the
 * reader which tiles the range does not reach.
 *
 * A `select` filter with NO `target_column` resolves inactive everywhere: a
 * filter that cannot say what it scopes to should not apply to everything.
 */
export function resolveFilterScope(
  filter: NativeFilterSpec,
  charts: Record<string, ChartSpec>,
  datasets: Record<string, DatasetSpec>,
): { chartId: string; active: boolean }[] {
  if (filter.scope_chart_uuids.length > 0) {
    return filter.scope_chart_uuids.map((chartId) => ({ chartId, active: true }));
  }
  if (filter.kind === "time_range") {
    return Object.keys(charts).map((chartId) => ({ chartId, active: true }));
  }
  if (filter.kind === "range") {
    // A parameter is declared BY a dataset, so the scope is every chart over
    // a dataset that declares both bounds — which, since the pair is named
    // on one dataset, is that dataset's charts and no other.
    const lowName = filter.target_parameter_low;
    const highName = filter.target_parameter_high;
    return Object.entries(charts).map(([chartId, chart]) => {
      const dataset = datasets[chart.dataset_uuid];
      const declared = new Set(dataset?.parameters.map((p) => p.name) ?? []);
      const active = !!lowName && !!highName && declared.has(lowName) && declared.has(highName);
      return { chartId, active };
    });
  }
  const targetColumn = filter.target_column;
  return Object.entries(charts).map(([chartId, chart]) => {
    if (!targetColumn) return { chartId, active: false };
    const dataset = datasets[chart.dataset_uuid];
    const active =
      !!dataset &&
      [...dataset.columns, ...(dataset.cohort_filters?.columns ?? [])].some(
        (c) => c.name === targetColumn,
      );
    return { chartId, active };
  });
}

// ── URL round-trip (`?filters=` on `/d/$slug`) ──────────────────────────────

/** `FilterState` -> a URL-safe string. Plain JSON, `encodeURIComponent`'d —
 *  no need for anything denser than that; a dashboard's filter state is a
 *  handful of column/value pairs, not a payload size concern. */
export function serializeFilterState(state: FilterState): string {
  return encodeURIComponent(JSON.stringify(state));
}

/** Every operator the backend's `RuntimeFilterValue.op` accepts, as runtime
 *  VALUES — `RuntimeFilterOp` is a type and erases at compile time, so a
 *  runtime guard needs its own copy. Written as the keys of a
 *  `satisfies Record<RuntimeFilterOp, true>` object rather than a bare array
 *  so the copy cannot silently drift: the generated union gaining an operator
 *  (a missing key) or losing one (an extra key) fails typecheck here instead
 *  of quietly filtering out a filter the backend would have accepted. */
const RUNTIME_FILTER_OPS = new Set(
  Object.keys({
    eq: true,
    neq: true,
    in: true,
    not_in: true,
    gt: true,
    gte: true,
    lt: true,
    lte: true,
    between: true,
    is_null: true,
    is_not_null: true,
  } satisfies Record<RuntimeFilterOp, true>),
);

function isRuntimeFilterValue(entry: unknown): entry is RuntimeFilterValue {
  if (typeof entry !== "object" || entry === null) return false;
  const candidate = entry as Partial<RuntimeFilterValue>;
  return (
    typeof candidate.column === "string" &&
    candidate.column.length > 0 &&
    typeof candidate.op === "string" &&
    RUNTIME_FILTER_OPS.has(candidate.op)
  );
}

/** The inverse of `serializeFilterState`, defensive against a stale or
 *  hand-edited link: a missing, malformed, or shape-invalid `raw` value
 *  degrades to `emptyFilterState()` rather than throwing — a broken filter
 *  param should never be the reason a shared dashboard link fails to load. */
export function parseFilterState(raw: string | undefined): FilterState {
  if (!raw) return emptyFilterState();
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (typeof parsed !== "object" || parsed === null) return emptyFilterState();
    const candidate = parsed as Partial<FilterState>;
    const start =
      typeof candidate.time_range_start === "string" ? candidate.time_range_start : null;
    const end = typeof candidate.time_range_end === "string" ? candidate.time_range_end : null;
    // Both bounds, of the SAME recognized precision, or neither. Two
    // failures collapse to the same "drop both" outcome here:
    //
    // - A one-sided pair. The backend compiler REFUSES a one-sided override
    //   (`PartialTimeRangeOverride`) rather than silently widening the window
    //   to the chart's authored range — that refusal is the guarantee, and
    //   this keeps a stale or hand-edited link from tripping it on every
    //   chart at once.
    // - A mixed-precision pair (a bare day paired with a real moment) or a
    //   string that matches neither recognized shape. The backend refuses
    //   this too (`MixedTimeRangePrecision`) for the same reason: there is no
    //   honest single meaning to invent. `isValidCustomRange` is the same
    //   shape check `lib/time-range.ts`'s custom-range control runs before
    //   ever constructing a pair, reused here so a value this app would never
    //   PRODUCE itself still can't reach `/query` and 422 the whole batch.
    //
    // `setTimeRange`/`clearTimeRange` only ever write a matching pair
    // together, so anything else is malformed input by definition; dropping
    // it means "no time filter" rather than inventing a reading for it.
    const bothBounds = isValidCustomRange(start, end);
    const parameters: Record<string, number | null> = {};
    if (typeof candidate.parameters === "object" && candidate.parameters !== null) {
      for (const [name, value] of Object.entries(candidate.parameters)) {
        // A shared link is untrusted text: only a finite number is a bound the
        // service would accept, so anything else is dropped here rather than
        // turned into a 422 on every tile of the dashboard.
        if (value === null || (typeof value === "number" && Number.isFinite(value)))
          parameters[name] = value;
      }
    }
    return {
      // Per ELEMENT, not just `Array.isArray`: FastAPI validates
      // `list[RuntimeFilterValue]` and 422s the WHOLE `/query` call on one
      // malformed entry, erroring every chart. Drop the bad entry, keep the rest.
      values: Array.isArray(candidate.values) ? candidate.values.filter(isRuntimeFilterValue) : [],
      time_range_start: bothBounds ? start : null,
      time_range_end: bothBounds ? end : null,
      parameters,
    };
  } catch {
    return emptyFilterState();
  }
}

// ── authored-filter constraints (why a filtered chart can come back empty) ──

/** One AUTHORED filter a chart carries on a column the reader is currently
 *  filtering. `label` is reader-facing, e.g. `country = US`. */
export interface AuthoredConstraint {
  column: string;
  label: string;
}

/** Reader-facing rendering of each authored operator. A `Record` over the
 *  generated union rather than a `switch`, so the backend adding an operator
 *  fails typecheck here instead of silently rendering `undefined`. */
const OPERATOR_LABEL = {
  EQUALS: (v: string) => `= ${v}`,
  NOT_EQUALS: (v: string) => `is not ${v}`,
  LESS_THAN: (v: string) => `< ${v}`,
  GREATER_THAN: (v: string) => `> ${v}`,
  LESS_THAN_OR_EQUAL: (v: string) => `<= ${v}`,
  GREATER_THAN_OR_EQUAL: (v: string) => `>= ${v}`,
  IN: (v: string) => `in ${v}`,
  NOT_IN: (v: string) => `not in ${v}`,
  IS_NULL: () => "is empty",
  IS_NOT_NULL: () => "is set",
} satisfies Record<FilterOperator, (value: string) => string>;

/**
 * The authored constraints a chart carries on columns the reader is filtering
 * right now.
 *
 * Runtime filters NARROW a chart's authored filters, they never replace them
 * (see the backend `FilterState` docstring for why: replacing would widen a
 * chart past its author's intent while keeping the author's title). The
 * honest consequence is that a chart authored `country = US`, viewed under a
 * `country = IL` selection, correctly returns nothing.
 *
 * What is NOT acceptable is leaving the reader to guess. Superset renders
 * that as a bare empty panel; this exists so the BI app can say WHICH
 * authored constraint is also in play, both as a standing note and in the
 * empty state. It reports the constraint as a FACT rather than trying to
 * decide whether it truly contradicts the selection: `country = US` under
 * `country in (US, IL)` narrows perfectly sensibly, and calling that a
 * conflict would be a lie. Either way the reader wants the same sentence.
 *
 * `SqlFilter` entries are skipped: raw SQL text names no column this can
 * match a selection against, and guessing one out of a string would be
 * exactly the kind of silent inference this module refuses.
 */
export function authoredConstraintsOnFilteredColumns(
  chart: ChartSpec,
  state: FilterState,
): AuthoredConstraint[] {
  const filteredColumns = new Set((state.values ?? []).map((v) => v.column));
  if (filteredColumns.size === 0) return [];
  const authored = chart.query.filters ?? [];
  const out: AuthoredConstraint[] = [];
  for (const filter of authored) {
    if (filter.kind !== "simple") continue;
    if (!filteredColumns.has(filter.subject)) continue;
    const render = OPERATOR_LABEL[filter.operator];
    out.push({
      column: filter.subject,
      label: `${filter.subject} ${render(filter.comparator ?? "")}`.trim(),
    });
  }
  return out;
}

/**
 * True iff this chart cannot bind the reader's date range, so the tile shows
 * a time-independent number while the rest of the dashboard is narrowed.
 *
 * `orchestration.scope_filters_for_chart` drops the range for exactly these
 * charts, because `compile_chart_query` refuses a real time range with no
 * temporal column (`MissingTemporalColumn`). Dropping it is right, but a tile
 * that quietly ignores the reader's filter is "trusted label, wrong scope", so
 * the card says so.
 *
 * This MIRRORS `compiler.resolve_temporal_column`: the same resolution order
 * (granularity_sqla, then x_axis for the timeseries family, then the dataset's
 * main_dttm_col) AND the same type gate. Both halves are load-bearing:
 * `echarts_timeseries_bar` is Superset's ordinary bar chart, so the x_axis rule
 * nominates the STRING category axis of every categorical bar chart.
 *
 * A mirror rather than a server flag only because every input, including each
 * column's `bq_type`, is already in `BiDashboardDetailResponse`. If the rule
 * grows a condition the client cannot see, make it a backend field.
 *
 * A missing dataset returns FALSE: "I cannot tell" must not render as "this
 * tile ignores your filter". `chartTruncatesTimeOfDay` fires only on a column
 * this function accepts, so the two never fire together.
 */
const TEMPORAL_BQ_TYPES = new Set(["DATE", "DATETIME", "TIMESTAMP"]);

export function chartIgnoresDateRange(chart: ChartSpec, dataset: DatasetSpec | undefined): boolean {
  if (!dataset) return false;
  if (dataset.cohort_filters) return false;
  const nominated = nominateTemporalColumn(chart, dataset);
  if (!nominated) return true;
  const column = dataset.columns.find((c) => c.name === nominated);
  return !column || !TEMPORAL_BQ_TYPES.has(column.bq_type);
}

/**
 * True iff the reader's CURRENT time-range selection carries a time of day and
 * this chart's resolved temporal column is a plain `DATE`, so
 * `compile_chart_query` TRUNCATES the selection to day precision rather than
 * refusing it (see `_TIME_OF_DAY_TYPES` in `service/bi/core/compiler.py`: 46 of
 * the 67 date-aware charts resolve a `DATE`). This only tells the reader that
 * truncation happened on THIS tile. See `chartIgnoresDateRange` for how the two
 * relate.
 *
 * False when the selection has no time of day (every preset and plain custom
 * day range) or the dataset is missing ("I cannot tell" must never render as
 * "this tile is imprecise").
 */
export function chartTruncatesTimeOfDay(
  chart: ChartSpec,
  dataset: DatasetSpec | undefined,
  state: Pick<FilterState, "time_range_start" | "time_range_end">,
): boolean {
  if (!dataset) return false;
  if (boundPrecision(state.time_range_start ?? "") !== "datetime") return false;
  if (dataset.cohort_filters)
    return (
      dataset.cohort_filters.columns.find((c) => c.name === dataset.cohort_filters?.main_dttm_col)
        ?.bq_type === "DATE"
    );
  const nominated = nominateTemporalColumn(chart, dataset);
  if (!nominated) return false;
  const column = dataset.columns.find((c) => c.name === nominated);
  return column?.bq_type === "DATE";
}

/** The column a time bound WOULD apply against, before the type gate above —
 * `compiler._nominate_temporal_column`'s resolution order, exactly. */
function nominateTemporalColumn(chart: ChartSpec, dataset: DatasetSpec): string | null {
  if (chart.query.time?.granularity_sqla) return chart.query.time.granularity_sqla;
  const isTimeseries =
    chart.viz_type === "echarts_timeseries_bar" || chart.viz_type === "echarts_timeseries_line";
  if (isTimeseries && chart.query.x_axis) return chart.query.x_axis;
  return dataset.main_dttm_col ?? null;
}
